import { get, isEmpty } from 'lodash/fp';

import { AHM_CORPORATION } from '../../constants';
import { ProductType } from '../../../../types/product';
import buildFinancialProductCategories from '../build-financial-product-categories';
import config from '../../../../config';
import getDescriptionByProductId from '../../../../pages/product/components/meta/get-description-by-product-id';
import getProductName from '../../../../pages/product/util/get-product-name';
import ProductService from '../../../../models/product';
import UrlService from '../../../../models/url';
import type { FinancialProductSource } from '../build-financial-product-schema/types';

type BuildFinancialProductSourceFromRuntimeArgs = {
  product: ProductType;
  softBundleComplementaryProduct?: ProductType;
};

const getCanonicalProductUrl = (relativeUrl: string) => {
  const baseUrl = config.get('sales:url');
  return relativeUrl.startsWith('http') ? relativeUrl : `${baseUrl}${relativeUrl}`;
};

type FinancialProduct = {
  url: string;
  name: string;
  description: string;
};

const getPreparedProductDescription = ({
  combinedProductId,
  product,
}: {
  combinedProductId?: string;
  product: ProductType;
}) => {
  const descriptionByProductId = getDescriptionByProductId();
  return (
    (combinedProductId && get(`${combinedProductId}.description`, descriptionByProductId)) ||
    get(`${product.parentProductId || product.id}.description`, descriptionByProductId) ||
    ''
  );
};

const prepareProduct = (product: ProductType, softBundleComplementaryProduct?: ProductType): FinancialProduct => {
  const combinedProductId = softBundleComplementaryProduct
    ? ProductService.getCombinedId(product, softBundleComplementaryProduct)
    : undefined;
  const id = combinedProductId || product.id;

  return {
    description: getPreparedProductDescription({ combinedProductId, product }),
    name: getProductName(product, softBundleComplementaryProduct) || product.shortName || product.name,
    url: getCanonicalProductUrl(UrlService.getProductUrl(id)),
  };
};

const buildFinancialProductSourceFromRuntime = ({
  product,
  softBundleComplementaryProduct,
}: BuildFinancialProductSourceFromRuntimeArgs): FinancialProductSource => {
  const preparedProduct = prepareProduct(product, softBundleComplementaryProduct);

  return {
    areaServed: AHM_CORPORATION.AREA_SERVED,
    category: buildFinancialProductCategories({
      hospitalTier: product.hospitalTier,
      isSoftBundle: !isEmpty(softBundleComplementaryProduct),
      productType: product.type,
    }),
    mainEntityOfPageId: preparedProduct.url,
    provider: {
      name: AHM_CORPORATION.NAME,
      url: AHM_CORPORATION.URL,
    },
    ...preparedProduct,
  };
};

export default buildFinancialProductSourceFromRuntime;
